import {AdapterBase} from '@/common/core/adapterBase';
import {ApiPath} from '@/common/core/common';

interface ReservationOption {
  option_id: number;
  reservation_id: number;
  amount: number;
}

/**
 * API 呼び出しクラス
 */
export class ClientAdapter extends AdapterBase {

  /**
   * 仮登録を行う
   * @param mailAddress
   * @param password
   * @param name
   * @param tel
   * @param birthday
   * @param gender
   * @param genre
   * @param adminFlg
   */
  public tmpRegisterMember<T>(mailAddress: string, password: string, name: string, tel: string, birthday: string, gender: string, genre: string, adminFlg: boolean): Promise<T> {
    return this.request('POST', ApiPath.USERS, true, {email: mailAddress, password: password, name: name, tel: tel, birthday: birthday, gender: gender, genre: genre, admin_flg: adminFlg});
  }

  /**
   * HEADER : CSRFトークンの取得を行う。
   */
  public getProbe<T>(): Promise<{}> {
     return this.request('GET', ApiPath.PROBE_URI, true, {});
  }

  /**
   * ログインを行う。
   *
   * @param mailAddress
   * @param password
   */
  public loginSystem<T>(mailAddress: string, password: string, query: object): Promise<{status: string}> {
    return this.request('POST', ApiPath.LOGIN_URI, true,
      {email: mailAddress, password: password, query: query});
  }

  /**
   * ログアウトを行う。
   *
   */
  public logoutSystem<T>(): Promise<{result: {}}> {
    return this.request('DELETE', ApiPath.LOGOUT_URI, true, {});
  }

  /**
   * 本登録を行う。
   *
   * @param memberId
   * @param birthday
   * @param gender
   * @param genre
   */
  public register<T>(memberId: string, birthday: string, gender: string, genre: string): Promise<T> {
    return this.request('PUT', ApiPath.USERS, true,
      {birthday: birthday, gender: gender, genre: genre});
  }

  /**
   * 仮登録処理を完了する。
   *
   * @param query
   */
  public completeTmpRegister<T>(query: string[]): Promise<T> {
    return this.request('GET', ApiPath.USERS_CONFIRMATION, true, {query: query});

  }

  /**
   * ログイン中のユーザ情報を取得する。
   */
  public getCurrentUserInfo<T>(): Promise<{memberId: string, userName: string, status: number, serialCode: number, grade: string,}> {
    return this.request('GET', ApiPath.CURRENT_USER, true, {});
  }

  /**
   * スタジオ情報を取得する。
   *
   * @param store_id
   */
  public getStudioInfo<T>(store_id: number): Promise<{id:number, name: string, area: number, capacity: number, dayNormalPrice: number, dayCreditPrice: number, dayEarlyPrice: number, MidNormalPrice: number, MidCreditPrice: number, MidEarlyPrice: number, material:string, vertical: number, width: number}[]> {
    return this.request('GET', ApiPath.STUDIO_INFO, false, {store_id: store_id});
  }

  /**
   * すべてのスタジオ情報を取得する。（店舗選択ができるようになるまでの暫定対応）
   *
   */
  public getAllStudioInfo<T>(): Promise<{id:number, name: string, area: number, capacity: number, dayNormalPrice: number, dayCreditPrice: number, dayEarlyPrice: number, MidNormalPrice: number, MidCreditPrice: number, MidEarlyPrice: number, store_id: number, material:string, vertical: number, width: number, openingHour: string, closingHour: string, show_priority: number, is_midnight_open: boolean}[]> {
    return this.request('GET', ApiPath.STUDIO_INFO, false, {});
  }

  /**
   * 店舗情報を取得する
   *
   * @param storeId
   */
  public getStoreInfoById<T>(storeId: number): Promise<{id: number, store_name: string, address: string, closed_flg: boolean, onsite_pay_available: boolean, equipment_rentalable: boolean}> {
    return this.request('GET', ApiPath.STORES + '/' + storeId, false, {})
  }
  /**
   * すべての店舗情報を取得する
   *
   * @param storeId
   */
   public getAllStores<T>(): Promise<{id: number, store_name: string, address: string, closed_flg: boolean, onsite_pay_available: boolean, equipment_rentalable: boolean} []> {
    return this.request('GET', ApiPath.STORES, false, {})
  }



  /**
   * 予約情報を取得する
   */
  public getReservations<T>(date: string): Promise<{reserveStartTime: string, reserveEndTime: string, room_id: number, midFlg: boolean}[]> {
    return this.request('GET', ApiPath.RESERVATIONS, false, {date: date});
  }

  /**
   * スタジオの予約をする
   * @param roomId
   * @param startTime
   * @param endTime
   * @param midFlg
   * @param earlyFlg
   * @param howToPay
   * @param billing
   * @param couponCode
   * @param highLevelUsage
   * @param detailUsage
   * @param numberOfUsers
   * @param remarks
   * @param optionReserved
   * @param actualPrice
   * @param optionTotalPrice
   * @param isCommercialUse
   * @param options
   */
  public reserve<T>(roomId: number, startTime: string, endTime: string, midFlg: boolean, earlyFlg: boolean, howToPay: number, billing: number, couponId: number, highLevelUsage: string, detailUsage: string, numberOfUsers: number, remarks: string, optionReserved: boolean, actualPrice: number, optionTotalPrice: number, isCommercialUse: boolean, options: { id: number, amount: number, billing: number }[]): Promise<{id: number}> {
    return this.request('POST', ApiPath.RESERVE, true, {room_id: roomId, reserveStartTime: startTime, reserveEndTime: endTime, midFlg: midFlg, earlyFlg: earlyFlg, howToPay: howToPay, billing: billing, coupon_id: couponId, highLevelUsage: highLevelUsage, detailUsage: detailUsage, numberOfUsers: numberOfUsers, memo: remarks, has_option: optionReserved, studioBilling: actualPrice, optionBilling: optionTotalPrice, isCommercialUse: isCommercialUse, options: options});
  }

  /**
   * スタジオ用途を取得する
   */
  public getUsages<T>(): Promise<{id: number, usage: string, parentFlg: boolean, parentUsageId: number}[]>{
    return this.request('GET', ApiPath.USAGE, false, {})
  }

  /**
   * パスワード再設定用メールを送信する
   */
  public sendPasswordResetEmail<T>(mailAddress: string, redirectURL: string): Promise<{}>{
    return this.request('POST', ApiPath.PASSWORD_RESET_DEVISE, true, {email: mailAddress, redirect_url: redirectURL})
  }

  /**
   * パスワードを再設定する
   */
  public passwordReset<T>(password: string, passwordConfirmation: string): Promise<{}>{
    return this.request('PUT', ApiPath.PASSWORD_RESET, true, {password: password, password_confirmation: passwordConfirmation})
  }

  /**
   * スタジオ画像を取得する
   * （顧客用画面）
   */
  public getRoomImages<T>(roomId: number): Promise<{id: number, rooms_id: number, imageUrl: string, show_priority: number}[]> {
    return this.request('GET', ApiPath.ROOM_IMAGE, false, {rooms_id: roomId});
  }
  /**
   * スタジオ画像全てを取得する
   * （顧客用画面）
   */
  public getAllRoomImages<T>(): Promise<{id: number, rooms_id: number, imageUrl: string, show_priority: number}[]> {
    return this.request('GET', ApiPath.ROOM_IMAGE, false, {});
  }

  /**
   * 個人の予約情報を取得する
   *
   */
   public getPersonalReservationCount<T>(memberId: number, couponId: number): Promise<{reservationCount: number}> {
      return this.request("GET", ApiPath.RESERVATIONS_COUNT, true, {userId: memberId - 89, couponId: couponId});
  }
  /**
   * 予約をキャンセルする
   * @param id
   * @param cancelType
   */
  public cancelReservation<T>(id: number, cancelType: number, cancelPrice: number, cancelReason: string) {
    return this.request("DELETE", ApiPath.RESERVE, true, {id: id, cancelType: cancelType, cancelPrice: cancelPrice, cancelReason: cancelReason});
  }

  /**
   * pay.jpのトークンをユーザ情報と紐付けて保存する
   * @param payjpToken
   */
  public registerPayjpToken<T>(payjpToken: string) {
    return this.request('PUT', ApiPath.USERS_PAYJP_ID, true, {payjp_token: payjpToken})
  }

  /**
   * クレジットカード情報をPay.jpから取得する
   */
  public getCreditCardInfo<T>(): Promise<{id: string, brand: string, expMonth: string, expYear: string, last4: string, isDefaultCard: boolean, three_d_secure_status: string}[]> {
    return this.request('GET', ApiPath.USERS_PAYJP, true, {});
  }

  /**
   * クレジットカードをPay.jpから削除する
   * @param id
   */
  public deleteCreditCard(id: string) {
    return this.request('DELETE', ApiPath.USERS_CREDIT_CARDS, true, {id: id});
  }

  /**
   * クレジットカードをPay.jpから削除する
   * @param id
   */
  public setDefaultCreditCard(id: string) {
    return this.request('PUT', ApiPath.USERS_CREDIT_CARDS, true, {id: id, default: true});
  }

    /**
     * IDで指定したRoomの情報を取得する
     * @param room_id
     */
  public getRoomById<T>(room_id: number): Promise<{id:number, name: string, area: number, capacity: number, dayNormalPrice: number, dayCreditPrice: number, dayEarlyPrice: number, MidNormalPrice: number, MidCreditPrice: number, MidEarlyPrice: number, store_id: number, material:string, vertical: number, width: number, openingHour: string, closingHour: string, show_priority: number, is_midnight_open: boolean}> {
    return this.request('GET', ApiPath.STUDIO_INFO, false, {id: room_id});
  }
  /**
   * オプション参照情報を取得する
   */

   public getStoreOptionRefs<T>(storeId: number): Promise<{id: number, store_id: number, option_id: number}[]> {
    return this.request('GET', ApiPath.STORE_OPTION_REFS, false, {store_id: storeId});
  }
  /**
   * オプション参照情報を取得する
   * （顧客用画面）
   */
   public getOptions<T>(optionId: number): Promise<{id: number, option_id: number, name: string, price: number, unit: number, description: string, max_rental_amount: number, image: string, memo: string, category_id: number}> {
    return this.request('GET', ApiPath.OPTIONS, false, {option_id: optionId});
  }
  /**
   * オプションのカテゴリー情報を取得する
   */
   public getOptionCategories<T>(): Promise<{id: number, category_id: number, name: string}[]> {
    return this.request('GET', ApiPath.OPTION_CATEGORIES, false, {});
  }

    /**
   * 指定した予約のオプション情報を取得する
   * @param reservation_id
   */
    public getReservationOption<T>(reservation_id: number): Promise<{ reservation_options: { id: number, reservation_id: number, option_id: number, amount: number, created_at: string, updated_at: string, optionBilling: number, name: string }[] }> {
      return this.request('GET', ApiPath.GET_RESERVATION_OPTIONS, false, { id: reservation_id });
    }
  
    /**
     */
    public getCoupon<T>(code: string): Promise<{ id: number; code: string; name: string; valid_from: string; valid_to: string; is_deleted: number; discount_target_service: number; is_rate: boolean; discount_amount: number; available_number_of_times_per_user: number; required_usage_min: number}[]> {
      return this.request('GET', ApiPath.COUPONS, true, {code: code});
    }

  /**
   * 個人の予約情報を取得する
   *
   */
   public getPersonalReservations<T>(memberId: number): Promise<{id: number, reserveStartTime: string, reserveEndTime: string, billing: number, cancelDate: string, cancelType: number, roomName: string, midFlg: boolean, reserveStatus: number, has_option: boolean, isCommercialUse: boolean}[]> {
    return this.request("GET", ApiPath.RESERVATIONS, false, {user_id: memberId,});
   }
  
  /**
   * ユーザのメールアドレスを取得する（クレカ登録時の3Dセキュア対応）
   * @param memberId
   * @returns 
   */
  public getUserEmail<T>(memberId: string): Promise<{email: string}> {
    return this.request('GET', ApiPath.USER_EMAIL, true, {memberId: memberId})
  }
}
